import React from 'react';
import {
  MonoKicker,
  GoldRule,
  PosterHeadline,
  SerifStory,
  GoldClause,
  GoldCTA,
  CtaMicroline,
  Section,
  SectionMarker,
  Reveal,
  RevealHairline,
  Device,
  APP_CONNECTIONS,
} from '../index';

/**
 * §03 RUN IT WITH ANYONE — widen the invitation without replacing the romance.
 *
 * Converts the visitor who just thought "beautiful, but I'm not in a
 * relationship." The romance stays the anchor; this band is a single pivot.
 *
 * Marketing priority is fixed by the plan of record and the copy honours it:
 * best friends lead, college roommates second. Family, siblings, parents and
 * coworkers are NOT marketed — family in particular is held until cohabitation
 * suppression ships. They appear below only as neutral connection types the
 * app supports, with no benefit copy, no imagery and no headline. That is the
 * line between "supports" and "markets"; do not let copy drift across it.
 *
 * No pricing figures anywhere. The site's job is App Store downloads, and a
 * price introduces cost before value. "First connection free" is framed as a
 * grant, not a limit, and is the only commercial fact on the page.
 */

// Every entry is accent + glyph + word. Never colour alone.
//
// THREE chips, not four. FAMILY and COWORKER are deliberately absent from
// site-composed markup: a chip on a marketing page is an invitation however
// neutrally it is styled, and the plan of record holds family until
// cohabitation suppression ships. ANYONE ELSE maps to the app's real "Other"
// connection type, so nothing here is invented. Three also composes better —
// four reads as a taxonomy, three as a gesture.
//
// ANYONE ELSE uses --cp-violet-soft #C08CFF rather than the canonical
// #A855F7, which measures 4.93:1 on canvas and 4.54:1 on surface — passing,
// but effectively on the line. #C08CFF sits at 7.84 / 8.16 / 7.22 and reads
// as a peer of coral and teal rather than the dim one.
//
// Never solve a contrast problem by borrowing a token from inside the
// identity system: #60A5FA is --cp-locked and means locked/free-tier, so
// using it as a connection accent would be an encoding collision — a worse
// bug than the contrast it was reached for.
const CONNECTION_TYPES = [
  { glyph: '♥', word: 'PARTNER', color: 'var(--cp-coral)' },
  { glyph: '●●', word: 'FRIEND', color: '#2DD4BF' },
  { glyph: '★', word: 'ANYONE ELSE', color: 'var(--cp-violet-soft)' },
];

export function FriendMode() {
  return (
    <Section id="anyone" labelledBy="anyone-h" tone="canvas">
      <SectionMarker className="mb-10">§ 03 · Everyone else</SectionMarker>

      <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
        {/* ---- type column: 4 of 12 ---- */}
        <div className="lg:col-span-4">
          <Reveal>
            <GoldRule />
            <MonoKicker className="mt-2">NOT ONLY FOR COUPLES</MonoKicker>
            <PosterHeadline
              as="h2"
              id="anyone-h"
              size="section"
              className="mt-3.5"
              lines={[{ text: 'Run it with' }, { text: 'anyone', outline: true }]}
            />
          </Reveal>

          <Reveal delay={120}>
            <SerifStory className="mt-[18px] max-w-[42ch]">
              Your oldest friend. Your college roommate. Anyone whose life has been running
              alongside yours{' '}
              <GoldClause>longer than either of you realised.</GoldClause>
            </SerifStory>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-8 max-w-[46ch] text-[length:var(--fs-body)] leading-[1.6] text-ink-2">
              Pick someone, both of you approve the connection, and the same map draws itself
              — every time your paths crossed before you were in the same room.
            </p>
          </Reveal>

          {/* ---- data rows ---- */}
          <div className="mt-10" data-testid="anyone-rows">
            <Reveal delay={280}>
              <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
                {/* EXTRA is load-bearing. "First connection free" silently
                    claims the first connection of any kind is free, which is
                    a different and unverified proposition. */}
                <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
                  First extra connection
                </span>
                <span
                  aria-hidden="true"
                  className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
                />
                <span className="cp-mono text-[length:var(--fs-mono-value)] text-gold-400">
                  Free
                </span>
              </div>
            </Reveal>
            <RevealHairline delay={280} />
            <Reveal delay={340}>
              <div className="mt-3 flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
                <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
                  Both people approve
                </span>
                <span
                  aria-hidden="true"
                  className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
                />
                <span className="cp-mono text-[length:var(--fs-mono-value)] text-paper">
                  Always
                </span>
              </div>
            </Reveal>
            <RevealHairline delay={340} />
          </div>

          <Reveal delay={420} className="mt-9 flex flex-col items-start gap-4">
            <GoldCTA data-testid="anyone-download" fullWidth className="min-[560px]:w-auto">
              Discover Your Crossed Paths
            </GoldCTA>
            <CtaMicroline className="w-full" />
          </Reveal>
        </div>

        {/* ---- device + types + privacy panel: 7 of 12 ---- */}
        <div className="lg:col-span-7 lg:col-start-6">
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-10">
            <Reveal delay={160} className="flex-none">
              <Device
                src={APP_CONNECTIONS.src}
                alt={APP_CONNECTIONS.alt}
                width={APP_CONNECTIONS.width}
                height={APP_CONNECTIONS.height}
                className="w-[240px] md:w-[260px] lg:w-[300px]"
              />
              <p className="cp-mono mt-4 text-[length:var(--fs-mono)]">
                Your people · one globe each
              </p>
            </Reveal>

            {/* ---- connection-type strip ---- */}
            <Reveal delay={240} className="md:pt-2" data-testid="anyone-types">
              <p className="cp-mono text-[length:var(--fs-mono)]">
                <span className="text-gold-400">● GOLD = YOU</span> · THE ACCENT IS THEM
              </p>
              <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-3 md:flex-col md:gap-y-4">
                {CONNECTION_TYPES.map((t) => (
                  <li key={t.word} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="text-[13px] leading-none"
                      style={{ color: t.color }}
                    >
                      {t.glyph}
                    </span>
                    <span
                      className="cp-mono text-[length:var(--fs-mono)]"
                      style={{ color: t.color }}
                    >
                      {t.word}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* ---- the privacy invariant, verbatim and mandatory ---- */}
          <Reveal delay={320} className="mt-10" data-testid="anyone-privacy">
            <div className="rounded-[16px] border border-[color:var(--cp-hairline)] border-l-2 border-l-gold-400 bg-surface px-[22px] py-5">
              <p className="cp-mono text-[length:var(--fs-mono)] text-gold-400">▚ Privacy</p>
              {/* Body size, never fine print: a privacy guarantee rendered
                  small reads as a disclaimer. This is a feature. */}
              <p className="mt-3 text-[length:var(--fs-body)] leading-[1.6] text-paper-soft">
                They will see only the times and places your paths crossed — never your full
                location history.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

export default FriendMode;
